import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Table, Spinner, Button, Collapse } from "react-bootstrap";
import { FaDownload } from "react-icons/fa";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import "./NotificationReport.css";

// Helper to get school_type summary counts for the whole state
function getSchoolTypeSummary(districtData) {
  const codeToName = new Map([
    ["6", "Other State Govt. Managed"],
    ["8", "Other"],
  ]);

  const toTitleCase = (str) => {
    return String(str).split(' ').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join(' ');
  };

  const summary = {};
  districtData.forEach(d => {
    if (Array.isArray(d.schoolTypes)) {
      d.schoolTypes.forEach(st => {
        // Filter out undefined, null, or empty school types
        const rawValue = st.school_type;
        
        // Handle null/undefined by mapping to "Other"
        if (rawValue === null || rawValue === undefined || String(rawValue).trim() === '') {
          const key = "Other";
          if (!summary[key]) summary[key] = 0;
          summary[key] += st.count;
          return;
        }
        
        const rawCode = String(rawValue).trim();
        const mappedName = codeToName.get(rawCode) || rawValue;
        const key = toTitleCase(String(mappedName).trim());
        const keyLower = key.toLowerCase();
        if (keyLower !== 'undefined' && keyLower !== 'null') {
          if (!summary[key]) summary[key] = 0;
          summary[key] += st.count;
        }
      });
    }
  });
  // Merge numeric code keys (e.g. '8') into 'Other' to avoid duplicate buckets
  if (summary['8']) {
    summary['Other'] = (summary['Other'] || 0) + summary['8'];
    console.log(`🔀 Merged ${summary['8']} items from key '8' into 'Other'`);
    delete summary['8'];
  }

  return summary;
}

// Helper to create ministry-style grouped counts by school_type code
function getMinistrySummary(districtData) {
  const normalizeName = (value) =>
    String(value || "")
      .trim()
      .toLowerCase()
      .replace(/\./g, "")
      .replace(/\s+/g, " ");

  const codeToName = new Map([
    ["6", "other state govt managed"],
    ["8", "other"],
  ]);

  const governmentNames = new Set([
    "department of education",
    "tribal welfare department",
    "local body",
    "other state govt managed",
    "minority affairs department",
    "social welfare department",
    "ministry of labour",
    "sainik school",
    "railway school",
    "central tibetan school",
    "other central govt/psu schools",
    "other central govt/psu schools**",
  ]);

  const aidedNames = new Set([
    "government aided",
    "partially govt aided",
    "partially government aided"
  ]);
  const summary = {
    government: 0,
    aided: 0,
    privateOther: 0,
  };

  districtData.forEach(d => {
    if (Array.isArray(d.schoolTypes)) {
      d.schoolTypes.forEach(st => {
        const rawValue = st.school_type;
        const count = Number(st.count) || 0;
        
        // Handle null/undefined by putting in privateOther
        if (rawValue === null || rawValue === undefined || String(rawValue).trim() === '') {
          summary.privateOther += count;
          return;
        }
        
        const rawCode = String(rawValue).trim();
        const mappedName = codeToName.get(rawCode);
        const normalizedName = mappedName || normalizeName(rawValue);
        if (normalizedName === "undefined" || normalizedName === "null") {
          summary.privateOther += count;
          return;
        }

        if (rawCode === "98") {
          summary.privateOther += count;
        } else if (governmentNames.has(normalizedName)) {
          summary.government += count;
        } else if (aidedNames.has(normalizedName)) {
          summary.aided += count;
        } else {
          summary.privateOther += count;
        }
      });
    }
  });

  return summary;
}

// Inside the admin report shell this is rendered directly, with the state and
// the already-fetched counts handed in as props and Back wired to onBack —
// outside it, the state comes from the URL and the counts from router state.
function DistrictNotificationReport({ embedded = false, stateName: stateProp = null, data: dataProp = null, onBack = null }) {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const stateName = stateProp || params.stateName;
  const passedData = dataProp || location.state;
  
  const [loading, setLoading] = useState(!passedData);
  const [totalNotifications, setTotalNotifications] = useState(passedData?.totalCount || 0);
  const [districtData, setDistrictData] = useState(passedData?.districtCounts || []);
  const [schoolTypeSummary, setSchoolTypeSummary] = useState({});
  const [ministrySummary, setMinistrySummary] = useState({ government: 0, aided: 0, privateOther: 0 });
  const [isOpen, setIsOpen] = useState(false);
  const [expandedDistricts, setExpandedDistricts] = useState({});
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    // Only fetch if data wasn't passed via navigation
    if (stateName && !passedData) {
      fetchDistrictReport();
    } else if (passedData?.districtCounts) {
      setSchoolTypeSummary(getSchoolTypeSummary(passedData.districtCounts));
      setMinistrySummary(getMinistrySummary(passedData.districtCounts));
    }
  }, [stateName, passedData]);

  const fetchDistrictReport = async () => {
    try {
      const response = await fetch('https://ecoclubs.education.gov.in/api/school-notifications/districts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ state: stateName })
      });
      const result = await response.json();
      if (result.status === 'success') {
        setDistrictData(result.districtCounts);
        setTotalNotifications(result.totalCount);
        setSchoolTypeSummary(getSchoolTypeSummary(result.districtCounts));
        setMinistrySummary(getMinistrySummary(result.districtCounts));
      }
    } catch (error) {
      console.error('Error fetching district report:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleDistrict = (index) => {
    setExpandedDistricts(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  // Download the school-wise notification list for this state as Excel
  const handleDownloadSchoolWiseExcel = async () => {
    setDownloading(true);
    try {
      const response = await fetch('https://ecoclubs.education.gov.in/api/school-notifications/all-schools-by-state', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ state: stateName })
      });
      const result = await response.json();

      if (result.status !== 'success') {
        toast.error('Failed to fetch school-wise notification list.');
        return;
      }

      const schools = (result.schools || []).filter(s => s.school_name);
      if (schools.length === 0) {
        toast.warning(`No schools with notifications found in ${stateName}`);
        return;
      }

      const wsData = [
        ["#", "School Name", "UDISE ID", "District", "Block Name", "School Type"],
        ...schools.map((school, idx) => [
          idx + 1,
          school.school_name,
          school.udise_id || school.udiseCode || '',
          school.district || '',
          school.eduBlockName || '',
          school.school_type || 'N/A'
        ])
      ];
      const ws = XLSX.utils.aoa_to_sheet(wsData);
      const wb = XLSX.utils.book_new();
      // Sheet names are capped at 31 chars and cannot contain : \ / ? * [ ]
      XLSX.utils.book_append_sheet(wb, ws, "School Wise Notifications");
      XLSX.writeFile(wb, `School_Wise_Notifications_${stateName.replace(/\s+/g, '_')}.xlsx`);
      toast.success(`Downloaded ${schools.length} schools`);
    } catch (error) {
      console.error('Error downloading school-wise report:', error);
      toast.error('Failed to download school-wise report.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      <div className="notification-report-container">
        <div className="container-fluid">
          <div className="report-header">
            <div className="report-header-actions">
              <Button
                variant="success"
                className="back-button-custom"
                onClick={() => (embedded && onBack ? onBack() : navigate('/notification-report'))}
              >
                ← Back
              </Button>
              <Button
                variant="success"
                className="back-button-custom"
                onClick={handleDownloadSchoolWiseExcel}
                disabled={downloading}
              >
                {downloading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Preparing...
                  </>
                ) : (
                  <>
                    <FaDownload className="me-2" /> Download School-Wise List
                  </>
                )}
              </Button>
            </div>
            <h1 className="report-title">{stateName}- District-Wise Notifications Report</h1>
          </div>

          <div className="total-notifications-card">
            <h3>TOTAL NOTIFICATIONS IN {stateName.toUpperCase()}</h3>
            <h2 className="total-count">{totalNotifications}</h2>
          </div>

          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="success" />
              <p className="mt-3">Loading data...</p>
            </div>
          ) : (
            <>
              {/* Ministry Report Summary */}
              <div className="school-type-section">
                <button
                  className="accordion-toggle"
                  onClick={() => setIsOpen(!isOpen)}
                  aria-expanded={isOpen}
                >
                  <span className="accordion-title">
                    Ministry Report (3 Categories)
                  </span>
                  <span className={`accordion-arrow ${isOpen ? 'open' : ''}`}>
                    ▼
                  </span>
                </button>

                <Collapse in={isOpen}>
                  <div className="district-cards-grid">
                    <div className="district-card">
                      <div className="district-card-name">GOVERNMENT SCHOOLS</div>
                      <div className="district-card-count">{ministrySummary.government}</div>
                    </div>
                    <div className="district-card">
                      <div className="district-card-name">GOVERNMENT AIDED SCHOOLS</div>
                      <div className="district-card-count">{ministrySummary.aided}</div>
                    </div>
                    <div className="district-card">
                      <div className="district-card-name">PRIVATE AND OTHER SCHOOLS</div>
                      <div className="district-card-count">{ministrySummary.privateOther}</div>
                    </div>
                  </div>
                </Collapse>
              </div>

              {/* School Type Summary Cards with Accordion */}
              {Object.keys(schoolTypeSummary).length > 0 && (
                <div className="school-type-section">
                  <button 
                    className="accordion-toggle"
                    onClick={() => setIsOpen(!isOpen)}
                    aria-expanded={isOpen}
                  >
                    <span className="accordion-title">
                      School Type Categories ({Object.keys(schoolTypeSummary).length})
                    </span>
                    <span className={`accordion-arrow ${isOpen ? 'open' : ''}`}>
                      ▼
                    </span>
                  </button>
                  
                  <Collapse in={isOpen}>
                    <div className="district-cards-grid">
                      {Object.entries(schoolTypeSummary).map(([type, count]) => (
                        <div key={type} className="district-card">
                          <div className="district-card-name">{type.toUpperCase()}</div>
                          <div className="district-card-count">{count}</div>
                        </div>
                      ))}
                    </div>
                  </Collapse>
                </div>
              )}

              {/* District Table */}
              <div className="report-table-container">
                <Table responsive bordered hover className="notification-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>DISTRICT</th>
                      <th>NOTIFICATIONS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {districtData.map((district, index) => (
                      <React.Fragment key={index}>
                        <tr 
                          onClick={() => toggleDistrict(index)}
                          style={{ cursor: 'pointer' }}
                          className="district-row"
                        >
                          <td>{index + 1}</td>
                          <td className="district-name">
                            <span className={`district-accordion-arrow ${expandedDistricts[index] ? 'open' : ''}`}>
                              ▼
                            </span>
                            {' '}{district.district}
                          </td>
                          <td className="notification-count"><strong>{district.total || 0}</strong></td>
                        </tr>
                        {expandedDistricts[index] && district.schoolTypes && district.schoolTypes.length > 0 && (
                          <tr>
                            <td colSpan={3} style={{ padding: 0, backgroundColor: '#f8f9fa' }}>
                              <Collapse in={expandedDistricts[index]}>
                                <div style={{ padding: '20px' }}>
                                  <div className="district-cards-grid">
                                    {district.schoolTypes
                                      .filter(st => {
                                        const key = String(st.school_type ?? "").trim();
                                        return key !== '' && key.toLowerCase() !== 'undefined';
                                      })
                                      .map((st, stIndex) => (
                                        <div key={stIndex} className="district-card">
                                          <div className="district-card-name">{String(st.school_type ?? "").toUpperCase()}</div>
                                          <div className="district-card-count">{st.count}</div>
                                        </div>
                                      ))}
                                  </div>
                                </div>
                              </Collapse>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                    <tr className="total-row">
                      <td colSpan={2} className="text-end"><strong>Total:</strong></td>
                      <td className="notification-count"><strong>{totalNotifications}</strong></td>
                    </tr>
                  </tbody>
                </Table>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}

export default DistrictNotificationReport;
